import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { useApi } from "../../../api/ApiProvider";
import Api from "../../../axiosApi/api";
import { Employee, UpdateStateCommand } from "axiosApi/models";
import { useToastMessageQueue } from "../../../components/ToastMessages/ToastMessageProvider";
import intl from "components/i18n/ReactIntlWrapper";
import TimeOffRequestForm from "./TimeOffRequestForm";
import RequestedTimeOff from "./RequestedTimeOff";
import { addDays } from "date-fns";
import { getUserEntity } from "common/UserEntityProvider";
import { WorkflowProvider } from "../../Workflow/WorkflowContext";
import { handleAPIError } from "common/errorHandler";
import { useDialog } from "common/DialogProvider";
import WorkflowLogEntries from "../../Workflow/WorkflowLogEntries";
import { DateRange, WORKFLOW_MODULES } from "common/constants";
import { Stack, Card, CardContent, Typography, CardHeader, Box } from "@mui/material";
import TimeOffStockCarrousel from "../TimeOffStockCarrousel";

export type TimeOffInput = {
  id: number | null;
  range: DateRange | undefined;
  title: string | null;
  idEmployee: number;
  employeeName: string | null;
  employeeLastName: string | null;
  idWorkflowState: number | null;
  workflowStateName: string | null;
  changeLog: string | null;
  timeOffRequestTypeName: string | null;
  workFlowLabelKey: string | null;
  workflowStateColor: string | null;
  attachments?: any;
  employeeRegimeTimeOffTypes?: any;
  idEmployeeRegimeTimeOffType: number | null;
};

export type SearchResult = {
  totalRecords: number | null;
  queryResult: any;
};

const TimeOffRequest = ({ HRView }) => {
  const initialRange: DateRange = {
    from: new Date(),
    to: addDays(new Date(), 4),
  };
  const [comments, setComments] = useState('');

  const { id } = useParams();
  const idNumber = id && parseInt(id);

  const userDetails = getUserEntity();

  const [input, setInput] = useState<TimeOffInput | null>({
    id: null,
    range: initialRange,
    title: '',
    idEmployee: userDetails.entityId,
    employeeName: '',
    employeeLastName: '',
    idWorkflowState: null,
    workflowStateName: "",
    changeLog: "",
    idEmployeeRegimeTimeOffType: null,
    timeOffRequestTypeName: "",
    attachments: [],
    workFlowLabelKey: "",
    workflowStateColor: ""
  });

  const [requestType, setRequestType] = useState(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState({});
  const [employees, setEmployees] = useState<Employee[]>(null);
  const [employeeRegimeTimeOffTypes, setEmployeeRegimeTimeOffTypes] = useState<any[]>([]);
  const [timeOffStocks, setTimeOffStocks] = useState<any[]>([]);
  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const { openDialog } = useDialog();

  const navigate = useNavigate();

  const fetchTimeOffData = async (entityId: number, setLoadingState: boolean = true) => {
    if (!entityId || !api?.timeoffApi) return;
    if (setLoadingState) setLoading(true);
    try {
      const response = await api.timeoffApi.apiVversionEmployeeTimeOffRegimeEmployeeEmployeeIdGet(entityId, '1');
      if (response?.status === 200 && response?.data?.data) {
        setTimeOffStocks(response?.data?.data?.employeeTimeOffStocks);
        setEmployeeRegimeTimeOffTypes(response?.data?.data?.employeeRegimeTimeOffTypes);
      } else {
        setTimeOffStocks([]);
        setEmployeeRegimeTimeOffTypes([]);
      }
    } catch (err: any) {
      setTimeOffStocks([]);
      setEmployeeRegimeTimeOffTypes([]);
    } finally {
      if (setLoadingState) setLoading(false);
    }
  };

  const fetchRequestType = async () => {
    const response = await api.timeoffApi
      .apiVversionListTimeOffTypesByRegimeGet("1")
      .then((response) => {
        if (response.data.data) {
          setRequestType(response.data.data);
        } else if (response.data) {
          setRequestType(response.data);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
      });
  };

  const fetchEmployees = async () => {
    const response = await api.employeeApi
      .apiVversionEmployeeAllGet('1', 1, 100)
      .then((response) => {
        if (response.data.data) {
          setEmployees(response.data.data.queryResult);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRequestType();
    if (HRView) {
      fetchEmployees();
    }
    if (idNumber) {
      fetchTimeOffApprove(idNumber);
    } else if (!HRView) {
      fetchTimeOffData(userDetails.entityId);
    }
  }, []);

  useEffect(() => {
    if (HRView && input.idEmployee) {
      fetchTimeOffData(input.idEmployee);
    }
  }, [HRView, input.idEmployee, idNumber]);

  const fetchTimeOffApprove = async (idNumber: number) => {
    setLoading(true);
    try {
      const response = await api.timeoffApi
        .apiVversionEmployeeTimeOffRequestIdGet(idNumber, "1", {});

      if (response.data) {
        const employeeId = response.data?.data?.idEmployee;
        setInput({
          ...input,
          changeLog: response.data?.data?.changeLog,
          employeeName: response.data?.data?.employeeName,
          employeeLastName: response.data?.data?.employeeLastName,
          range: {
            from: response.data?.data?.from,
            to: response.data?.data?.to,
          },
          id: response.data?.data?.id,
          idEmployee: employeeId,
          idEmployeeRegimeTimeOffType: response.data?.data?.idEmployeeRegimeTimeOffType,
          idWorkflowState: response.data?.data?.idWorkflowState,
          timeOffRequestTypeName: response.data?.data?.timeOffRequestTypeName,
          title: response.data?.data?.title,
          workflowStateName: response.data?.data?.workflowStateName,
          workFlowLabelKey: response.data?.data?.workFlowLabelKey,
          workflowStateColor: response.data?.data?.workflowStateColor,
          attachments: response.data?.data?.attachments,
        });
        if (employeeId) {
          await fetchTimeOffData(employeeId, false);
        }
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      setTimeout(function () {
        navigate(-1);
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const fetchFile = async (
    timeOffId: number,
    fileName: string,
    employeeId: number
  ): Promise<string | null> => {
    try {
      const response = await api.timeoffApi.apiVversionTimeOffFileGet(
        '1',
        timeOffId,
        fileName,
        employeeId,
        {}
      );
      if (response.data) {
        return formatFile(response.data.data[fileName]);
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    }
    return null;
  };

  const validate = (input) => {
    let errors: any = {};
    if (!input.range) {
      errors.range = intl.get('validate.errors.isRequired');
    }
    if (!input.idEmployeeRegimeTimeOffType) {
      errors.idEmployeeRegimeTimeOffType = intl.get("validate.errors.isRequired");
    }
    setErrors({ ...errors });
    return Object.keys(errors).length > 0 ? errors : {};
  };

  const handleRequestChange = (e) => {
    if (e !== null) {
      setInput({ ...input, idEmployeeRegimeTimeOffType: e.id, title: e?.label || '' });
      setErrors(
        validate({
          ...input,
          idEmployeeRegimeTimeOffType: e.id,
          title: e.label,
        })
      );
    } else {
      setInput({ ...input, idEmployeeRegimeTimeOffType: null, title: '' });
      setErrors(
        validate({
          ...input,
          idEmployeeRegimeTimeOffType: e,
          title: '',
        })
      );
    }
  };

  const handleDaySelect = (date: DateRange | undefined) => {
    if (date) {
      setInput({ ...input, range: date });
      setErrors(
        validate({
          ...input,
          range: date,
        })
      );
    }
  };

  const handleEmployeeChange = (e) => {
    const inputVal = {
      ...input,
      idEmployee: e.id,
    };
    setInput({ ...inputVal });
  };

  const formatFile = (file) => {
    try {
      const base64String = file;
      const binaryString = atob(base64String);
      const byteArray = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        byteArray[i] = binaryString.charCodeAt(i);
      }
      const fileExtension = file.split('.').pop()?.toLowerCase();
      let mimeType = 'application/octet-stream';
      if (fileExtension === 'pdf') mimeType = 'application/pdf';
      else if (fileExtension === 'png') mimeType = 'image/png';
      else if (fileExtension === 'jpg' || fileExtension === 'jpeg') mimeType = 'image/jpeg';
      const blob = new Blob([byteArray], { type: mimeType });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error in formatting file:', error);
      return null;
    }
  };

const handleSave = async (workflowStateId: number, comments: string) => {
  setLoading(true);

  const validationErrors = validate(input);
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    setLoading(false);
    return;
  }

  // Construimos el payload común para crear/actualizar
  const timeOffCommand: any = {
    id: input.id,
    from: input.range.from,
    to: input.range.to,
    title: input.title,
    idEmployee: input.idEmployee,
    changeLog: input.changeLog,
    idWorkflowState: workflowStateId,
    idEmployeeTimeOffType: input.idEmployeeRegimeTimeOffType,
    comments
  };

  try {
    let response;
    if (!input.id) {
      response = await api.timeoffApi.apiVversionEmployeeTimeOffRequestPost(
        '1',
        JSON.stringify(timeOffCommand),
        input.attachments,
        {}
      );
      if (response?.data?.data?.id) {
        setInput(prev => ({ ...prev, id: response.data.data.id }));
      }
      var command: UpdateStateCommand = {
      comments: comments,
      workflowStateId: workflowStateId,
      entityId: response.data.data.id,
      moduleId: WORKFLOW_MODULES.TIME_OFF,
    };
    await api.workflowApi.apiVversionWorkflowUpdateStatePost("1", command);
    } else {
      response = await api.timeoffApi.apiVversionEmployeeTimeOffRequestPut(
        '1',
        JSON.stringify(timeOffCommand),
        input.attachments,
        [],
        {}
      );
    }
    const successKey = !input.id
      ? 'calendarEvent.toast.success.handleSave.add'
      : 'calendarEvent.toast.success.handleSave';
     navigate(-1);
  } catch (error) {
    handleAPIError(error, toast, validationErrors);
    setErrors({ ...validationErrors });
  } finally {
    setLoading(false);
  }
};

  const handleDownload = async (file) => {
    let downloadUrl;
    if (file instanceof File) {
      downloadUrl = URL.createObjectURL(file);
    } else {
      downloadUrl = await fetchFile(input.id, file, input.idEmployee);
    }
    if (typeof downloadUrl == 'string') {
      downloadFile(downloadUrl);
    }
    function downloadFile(url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name ? file.name : file;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleDelete = (index, file) => {
    const removeAttachment = () => {
      const temp = [...input.attachments];
      temp.splice(index, 1);
      setInput((prevInput) => ({
        ...prevInput,
        attachments: temp,
      }));
    };

    openDialog({
      type: "confirm",
      title: intl.get("delete.modal.title"),
      message: intl.get("delete.modal.timeoff.message"),
      confirmButton: {
        confirmLabel: intl.get("delete.modal.delete.button"),
        handleConfirm: async (message) => {
          try {
            if (file instanceof File) {
              removeAttachment();
              toast.success({
                body: intl.get("timeOff.toast.success.handleDelete"),
              });
            } else {
              await api.timeoffApi.apiVversionTimeOffFileDelete(
                "1",
                input.id,
                file,
                input.idEmployee,
                {}
              );
              removeAttachment();
              toast.success({
                body: intl.get("timeOff.toast.success.handleDelete"),
              });
            }
            message.handleClose(message);
          } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
            message.handleClose(message);
          }
        }
      },
      cancelButton: {
        cancelLabel: intl.get("delete.modal.cancel.button"),
        handleCancel: (message) => {
          message.handleClose(message);
        }
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <WorkflowProvider moduleId={2}>
        <Card>
          <CardHeader
            title={
              <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                {intl.get('timeOff.request.header')}
              </Typography>
            }
          />
          <CardContent>
            {(!HRView || (HRView && input.idEmployee && timeOffStocks.length > 0)) && <Box>
              <TimeOffStockCarrousel 
                timeOffStocks={timeOffStocks} 
                employeeRegimeTimeOffTypes={employeeRegimeTimeOffTypes}
                loading={loading}
              />
            </Box>}
            <Stack spacing={3}>
                  <TimeOffRequestForm
                    requestType={employeeRegimeTimeOffTypes?.map((type: any) => ({
                      id: type.id,
                      label: type.timeOffType.name,
                    }))}
                    input={input}
                    setInput={setInput}
                    errors={errors}
                    loading={loading}
                    handleRequestChange={handleRequestChange}
                    handleDaySelect={handleDaySelect}
                    handleSave={handleSave}
                    employees={employees}
                    handleEmployeeChange={handleEmployeeChange}
                    HRView={HRView}
                    setComments={setComments}
                    handleDownload={handleDownload}
                    handleDelete={handleDelete}
                  />
              <Card>
                <CardContent>
                  <WorkflowLogEntries idModule={WORKFLOW_MODULES.TIME_OFF} idEntity={input.id} />
                </CardContent>
              </Card>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography color="primary" variant="h6" sx={{ mb: 2 }}>
                    {intl.get('timeOff.requested.header')}
                  </Typography>
                  <RequestedTimeOff />
                </CardContent>
              </Card>
            </Stack>
          </CardContent>
        </Card>
      </WorkflowProvider>
    </Box>
  );
};

export default TimeOffRequest;
